import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';

type AssistantType = 'greeting' | 'success' | 'alert' | 'tip' | 'idle' | 'celebration';

interface AssistantAction {
    label: string;
    onClick: () => void;
}

interface CatAssistantContextType {
    message: string;
    type: AssistantType;
    isVisible: boolean;
    isCelebrating: boolean;
    action?: AssistantAction;
    say: (message: string, type?: AssistantType, duration?: number, action?: AssistantAction) => void;
    hide: () => void;
    celebrate: () => void;
    // Task-specific helpers
    taskMoved: (taskTitle: string, newStatus: string) => void;
    taskCompleted: (taskTitle: string) => void;
    taskCreated: (taskTitle: string) => void;
    commentAdded: () => void;
    subtaskCompleted: (remaining: number) => void;
    motivate: (pendingCount: number) => void;
    reservationReceived: (customerName: string) => void;
}

const CatAssistantContext = createContext<CatAssistantContextType | undefined>(undefined);

export const useCatAssistant = () => {
    const context = useContext(CatAssistantContext);
    if (!context) {
        throw new Error('useCatAssistant must be used within a CatAssistantProvider');
    }
    return context;
};

interface CatAssistantProviderProps {
    children: ReactNode;
}

// Fun messages for different events
const moveMessages = [
    '¡Eso es! Organizando como un pro 🎯',
    '¡Miau! Me encanta el orden 📋',
    '¡Genial movimiento! 🐱',
    '¡Arrastrando con estilo! ✨'
];

const completionMessages = [
    '¡MIAU! 🎊✨ ¡TAREA COMPLETADA! ¡Eres increíble!',
    '🎉🎉🎉 ¡LO LOGRASTE! ¡Celebremos juntos! 🐱',
    '¡ÉPICO! 🏆 ¡Otra tarea más conquistada!',
    '✨🎊 ¡MISIÓN CUMPLIDA! ¡Eres una máquina! 🚀'
];

const commentMessages = [
    '¡Excelente comunicación! 💬',
    '¡Me encanta el trabajo en equipo! 😸',
    '¡Buen feedback! La comunicación es clave 🔑'
];

const subtaskMessages = [
    '¡Paso a paso se logra todo! 📈',
    '¡Vas muy bien! Quedan {} por hacer',
    '¡Sigue así! Ya casi terminas 💪'
];

const motivateMessages = [
    '¡Hey! Tienes {} tareas esperándote. ¡Tú puedes! 💪',
    '🐱 {} tareas pendientes... ¡Vamos a por ellas!',
    '¿Vemos esas {} tareas? ¡Juntos lo logramos! 🎯',
    '¡Miau! {} tareas te esperan. ¡A trabajar! 🚀'
];

const taskCreatedMessages = [
    '¡Nueva tarea creada! ¡Vamos a conquistarla juntos! 🚀💪',
    '¡Miau! ¡Otra aventura épica comienza! 🎯✨',
    '¡Genial! ¡Una tarea más para demostrar tu grandeza! 🏆',
    '¡Excelente! ¡El progreso continúa con fuerza! 🔥',
    '¡Increíble! ¡Cada tarea es una oportunidad de brillar! 🌟'
];

const reservationMessages = [
    '¡ÉPICO! ✨ ¡Acaba de entrar una reserva nueva de {}! 🎉🐾',
    '¡MIAU! 🧨 ¡BOMBAZO! {} reservó ahora mismo. ¡A brillar! 🚀💎',
    '¡Waku Waku! 😻 {} se suma a la manada. ¡Nueva reserva detectada! 🎊',
    '¡A TODO RITMO! 🎷 {} confirmó su turno. ¡No paramos de crecer! ✨'
];

const getRandomMessage = (messages: string[]): string => {
    return messages[Math.floor(Math.random() * messages.length)];
};

export const CatAssistantProvider: React.FC<CatAssistantProviderProps> = ({ children }) => {
    const [message, setMessage] = useState('');
    const [type, setType] = useState<AssistantType>('idle');
    const [isVisible, setIsVisible] = useState(false);
    const [isCelebrating, setIsCelebrating] = useState(false);
    const [action, setAction] = useState<AssistantAction | undefined>(undefined);

    const hide = useCallback(() => {
        setIsVisible(false);
        setIsCelebrating(false);
        setTimeout(() => {
            setMessage('');
            setType('idle');
            setAction(undefined);
        }, 300);
    }, []);

    const say = useCallback((newMessage: string, newType: AssistantType = 'tip', duration: number = 5000, newAction?: AssistantAction) => {
        setMessage(newMessage);
        setType(newType);
        setAction(newAction);
        setIsVisible(true);
        setIsCelebrating(newType === 'celebration');

        if (duration > 0) {
            setTimeout(() => {
                hide();
            }, duration);
        }
    }, [hide]);

    const celebrate = useCallback(() => {
        say('¡Miau! ¡Excelente trabajo! 😺🎉', 'celebration', 4000);
    }, [say]);

    // Task-specific helper functions
    const taskMoved = useCallback((taskTitle: string, newStatus: string) => {
        const statusNames: { [key: string]: string } = {
            'todo': 'Por hacer',
            'in_progress': 'En progreso',
            'blocked': 'Bloqueada',
            'done': '¡Completada!'
        };
        const statusDisplay = statusNames[newStatus] || newStatus;
        say(`${getRandomMessage(moveMessages)} → ${statusDisplay}`, 'success', 3000);
    }, [say]);

    const taskCompleted = useCallback((taskTitle: string) => {
        // Epic celebration for task completion!
        say(getRandomMessage(completionMessages), 'celebration', 6000);
    }, [say]);

    const commentAdded = useCallback(() => {
        say(getRandomMessage(commentMessages), 'success', 3000);
    }, [say]);

    const subtaskCompleted = useCallback((remaining: number) => {
        if (remaining === 0) {
            say('¡Todas las subtareas completadas! 🎉', 'celebration', 4000);
        } else {
            const msg = getRandomMessage(subtaskMessages).replace('{}', String(remaining));
            say(msg, 'success', 3000);
        }
    }, [say]);

    const motivate = useCallback((pendingCount: number) => {
        if (pendingCount > 0) {
            const msg = getRandomMessage(motivateMessages).replace('{}', String(pendingCount));
            say(msg, 'tip', 5000);
        } else {
            say('¡No tienes tareas pendientes! ¡Disfruta tu tiempo! 😸🎉', 'celebration', 4000);
        }
    }, [say]);

    const taskCreated = useCallback((taskTitle: string) => {
        say(getRandomMessage(taskCreatedMessages), 'success', 4000);
    }, [say]);

    const reservationReceived = useCallback((customerName: string) => {
        const msg = getRandomMessage(reservationMessages).replace('{}', customerName);
        say(msg, 'celebration', 6000);
    }, [say]);

    return (
        <CatAssistantContext.Provider value={{
            message, type, isVisible, isCelebrating, action,
            say, hide, celebrate,
            taskMoved, taskCompleted, taskCreated, commentAdded, subtaskCompleted, motivate,
            reservationReceived
        }}>
            {children}
        </CatAssistantContext.Provider>
    );
};
